import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@clerk/clerk-react";
import { LucideLoader2, LucidePenLine, LucideCheck, LucideBell, LucideFileText } from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL } from "@/constants/api";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/layouts/PageHeader";
import { useSchool } from "@/context/SchoolContext";
import { generatePdf } from "@/reports/registry";
import { SIGNATURE_PLACEMENTS } from "@/reports/shared_utils/signaturePlacements";
import { PDFPreview } from "@/reports/report_components/PDFPreview";
import { formatGrade } from "@/utils/formatGrade";

// Resolve { teacher, principal } image data URIs into positioned overlays for a type.
function signatureOverlays(reportType, sigs) {
  const placements = SIGNATURE_PLACEMENTS[reportType] || {};
  const out = [];
  for (const role of ["teacher", "principal"]) {
    if (sigs?.[role] && placements[role]) {
      const { page, x, y, width, height } = placements[role];
      out.push({ data: sigs[role], page, x, y, width, height });
    }
  }
  return out;
}

export default function SignatureQueue() {
  const { memberships } = useSchool();
  const schoolId = memberships.find((m) => m.isAdmin)?.school?._id ?? memberships[0]?.school?._id;
  const { getToken } = useAuth();

  const [queue, setQueue] = useState(null);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null); // { folder, report }
  const [pdfUrl, setPdfUrl] = useState(null);
  const [rendering, setRendering] = useState(false);
  const [signing, setSigning] = useState(false);
  const [principalSigned, setPrincipalSigned] = useState(false);

  useEffect(() => {
    if (!schoolId) return;
    const ac = new AbortController();
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${BACKEND_URL}api/schools/${schoolId}/signature-queue`, {
          headers: { Authorization: `Bearer ${token}` }, signal: ac.signal,
        });
        if (!res.ok) throw new Error("Failed to load the signing queue");
        setQueue(await res.json());
      } catch (err) {
        if (err.name !== "AbortError") setError(err.message);
      }
    })();
    return () => ac.abort();
  }, [schoolId, getToken]);

  // Render a report's PDF with logos + resolved signatures.
  const renderReport = useCallback(async (folder, report) => {
    setRendering(true);
    setPdfUrl(null);
    try {
      const token = await getToken();
      const auth = { headers: { Authorization: `Bearer ${token}` } };
      const [reportRes, sigsRes, logosRes] = await Promise.all([
        fetch(`${BACKEND_URL}api/reports/${report._id}`, auth),
        fetch(`${BACKEND_URL}api/reports/${report._id}/signatures`, auth),
        fetch(`${BACKEND_URL}api/report-folders/${folder.folderId}/logos`, auth),
      ]);
      if (!reportRes.ok) throw new Error("Could not load the report");
      const reportData = await reportRes.json();
      const sigs = sigsRes.ok ? await sigsRes.json() : { teacher: null, principal: null };
      const logos = logosRes.ok ? (await logosRes.json()).logos ?? [] : [];
      setPrincipalSigned(!!sigs.principal);

      const overlays = [...logos, ...signatureOverlays(folder.reportType, sigs)];
      const url = await generatePdf(folder.reportType, reportData.data || {}, { logos: overlays });
      setPdfUrl(url);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setRendering(false);
    }
  }, [getToken]);

  const openReport = (folder, report) => {
    setSelected({ folder, report });
    renderReport(folder, report);
  };

  const signAsPrincipal = async (sign) => {
    if (!selected) return;
    setSigning(true);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/reports/${selected.report._id}/${sign ? "sign" : "unsign"}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ role: "principal" }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Could not sign the report.");
      }
      toast.success(sign ? "Report signed." : "Signature removed.");
      // Reflect in the queue list + re-render the preview.
      setQueue((q) => q?.map((f) => f.folderId !== selected.folder.folderId ? f : {
        ...f,
        reports: f.reports.map((r) => r._id === selected.report._id ? { ...r, principalSigned: sign } : r),
        pendingPrincipalCount: f.reports.filter((r) => r._id === selected.report._id ? !sign : !r.principalSigned).length,
      }));
      await renderReport(selected.folder, selected.report);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSigning(false);
    }
  };

  if (error) return <div className="container my-6 text-destructive">Error: {error}</div>;
  if (!queue) return <div className="container my-6 text-muted-foreground">Loading…</div>;

  const folders = [...queue].sort((a, b) =>
    (b.signatureRequestedAt ? 1 : 0) - (a.signatureRequestedAt ? 1 : 0) ||
    b.pendingPrincipalCount - a.pendingPrincipalCount
  );

  return (
    <div className="container my-6">
      <PageHeader
        title="Signature Queue"
        subtitle="Review each report and sign it as principal. Folders a teacher has asked you to sign are flagged."
      />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,380px)_1fr]">
        {/* Queue list */}
        <div className="space-y-4 max-h-[75vh] overflow-y-auto pr-1">
          {folders.length === 0 && <p className="text-sm text-muted-foreground">No reports in your school yet.</p>}
          {folders.map((f) => (
            <div key={f.folderId} className="rounded-xl border border-border bg-card">
              <div className="flex items-start justify-between gap-2 border-b border-border p-3">
                <div className="min-w-0">
                  <p className="truncate font-medium">{formatGrade(f.grade) || f.name || "Report Folder"}</p>
                  <p className="truncate text-xs text-muted-foreground">{f.owner?.name ?? "-"} · {f.reportCount} report{f.reportCount !== 1 ? "s" : ""}</p>
                </div>
                {f.signatureRequestedAt && (
                  <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-warning-bg px-2 py-0.5 text-[11px] font-medium text-warning-fg">
                    <LucideBell size={11} /> Requested
                  </span>
                )}
              </div>
              <ul className="divide-y divide-border">
                {f.reports.map((r) => (
                  <li key={r._id}>
                    <button
                      onClick={() => openReport(f, r)}
                      className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-accent ${
                        selected?.report._id === r._id ? "bg-accent" : ""
                      }`}
                    >
                      <span className="flex items-center gap-2 truncate">
                        <LucideFileText size={14} className="shrink-0 text-muted-foreground" />
                        <span className="truncate">{r.studentName || "Unnamed"}</span>
                      </span>
                      <span className="flex shrink-0 items-center gap-1.5 text-xs">
                        <span title="Teacher" className={r.teacherSigned ? "text-success" : "text-muted-foreground/40"}>T</span>
                        <span title="Principal" className={r.principalSigned ? "text-success" : "text-muted-foreground/40"}>P</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Preview + sign */}
        <div className="rounded-xl border border-border bg-muted/40 p-4">
          {!selected ? (
            <div className="flex h-[60vh] items-center justify-center text-sm text-muted-foreground">
              Select a report to review and sign.
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="font-medium">{selected.report.studentName || "Report"}</p>
                {principalSigned ? (
                  <Button variant="outline" size="sm" onClick={() => signAsPrincipal(false)} disabled={signing}
                    className="gap-1.5 border-success text-success hover:bg-success/10">
                    {signing ? <LucideLoader2 size={14} className="animate-spin" /> : <LucideCheck size={14} />} Signed
                  </Button>
                ) : (
                  <Button size="sm" onClick={() => signAsPrincipal(true)} disabled={signing || rendering}
                    className="gap-1.5">
                    {signing ? <LucideLoader2 size={14} className="animate-spin" /> : <LucidePenLine size={14} />} Sign as principal
                  </Button>
                )}
              </div>
              {rendering || !pdfUrl ? (
                <div className="flex h-[65vh] items-center justify-center rounded-lg bg-background">
                  <LucideLoader2 className="animate-spin text-muted-foreground" />
                </div>
              ) : (
                // react-pdf canvas (not an <iframe>) so it renders under the prod CSP.
                <div className="h-[65vh] overflow-hidden rounded-lg border border-border">
                  <PDFPreview src={pdfUrl} defaultPageCount={2} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
